import { useContentNavigation } from "contexts/ContentNavigationContext";
import { useMixContentByPkQuery } from "graphql/generated";
import { useRouter } from "next/router";
import { useCallback, useMemo, useRef } from "react";
import { ContentSection } from "types/app";

const CONTENT_DETAIL_STORAGE_KEY = "mix_prev_content_detail";

/**
 * 콘텐츠 상세 모달을 URL 해시 기반으로 제어하는 훅
 * SignInModal과 유사하게 `/#content?id={uuid}` 형태로 동작
 * ContentNavigationContext와 연동하여 이전/다음 네비게이션 지원
 */
export const useContentDetailModal = () => {
    const router = useRouter();
    const {
        setContentList,
        clearContentList,
        navigateTo,
        canNavigatePrev,
        canNavigateNext,
        currentContent: contextContent
    } = useContentNavigation();

    // 해시에서 content ID 파싱
    const contentId = useMemo(() => {
        const hash = router.asPath.split("#")[1];
        if (!hash || !hash.startsWith("content")) return null;

        const params = new URLSearchParams(hash.replace("content?", ""));
        return params.get("id");
    }, [router.asPath]);

    const isOpen = !!contentId;

    // 모달 오픈 방식 추적: openContentModal()로 열면 "card_click", URL 해시로 직접 열리면 "direct_link"
    const openMethodRef = useRef<"card_click" | "direct_link">("direct_link");
    // 카드 클릭으로 열릴 때의 섹션 컨텍스트 (content_click 이벤트에 사용)
    const contentSectionRef = useRef<ContentSection | undefined>(undefined);

    // 항상 Apollo query 실행하여 캐시 업데이트(북마크, 반응 등)가 반영되도록 함
    const { data, loading } = useMixContentByPkQuery({
        variables: { id: contentId! },
        skip: !contentId
    });

    // Apollo query 결과 우선 사용 (캐시 업데이트 반영)
    // 로딩 중에는 contextContent를 폴백으로 사용하여 즉시 표시
    const content =
        data?.mix_content_by_pk ??
        (contextContent?.id === contentId ? contextContent : null);

    // 모달 열기 (콘텐츠 목록과 함께)
    const openContentModal = useCallback(
        (
            id: string,
            contentList?: Mix_ContentFragment[],
            section?: ContentSection
        ) => {
            // 카드 클릭으로 명시적으로 열릴 때 open_method를 card_click으로 설정
            openMethodRef.current = "card_click";
            contentSectionRef.current = section;

            // 콘텐츠 목록이 제공되면 네비게이션 컨텍스트에 설정
            if (contentList && contentList.length > 0) {
                setContentList(contentList, id);
            }

            // 익스텐션에서 mix.day로 이동 시 platform=extension을 쿼리로 전달
            // mix.day에서 content_modal_open 이벤트 발생 시 origin platform 식별에 사용
            const isExtension =
                process.env.NEXT_PUBLIC_BUILD_TARGET === "extension";
            router.push(
                {
                    pathname: isExtension
                        ? "https://mix.day"
                        : router.pathname,
                    query: isExtension
                        ? { ...router.query, _src: "extension" }
                        : router.query,
                    hash: `content?id=${id}`
                },
                undefined,
                { scroll: false }
            );
        },
        [router, setContentList]
    );

    // 모달 닫기 (open_method 및 섹션 초기화)
    const closeContentModal = useCallback(async () => {
        openMethodRef.current = "direct_link";
        contentSectionRef.current = undefined;
        clearContentList();
        await router.push(
            {
                pathname: router.pathname,
                query: router.query,
                hash: null
            },
            undefined,
            { scroll: false }
        );
    }, [router, clearContentList]);

    // 이전/다음 콘텐츠로 네비게이션
    const handleNavigate = useCallback(
        (direction: "prev" | "next") => {
            const newId = navigateTo(direction);
            if (newId) {
                router.push(
                    {
                        pathname: router.pathname,
                        query: router.query,
                        hash: `content?id=${newId}`
                    },
                    undefined,
                    { scroll: false }
                );
            }
        },
        [router, navigateTo]
    );

    // 현재 콘텐츠 URL을 localStorage에 저장 (로그인 후 돌아오기 위함)
    const saveContentUrlForRedirect = useCallback(() => {
        if (contentId && typeof window !== "undefined") {
            localStorage.setItem(
                CONTENT_DETAIL_STORAGE_KEY,
                `#content?id=${contentId}`
            );
        }
    }, [contentId]);

    // localStorage에서 저장된 URL 가져오기
    const getSavedContentUrl = useCallback(() => {
        if (typeof window === "undefined") return null;
        return localStorage.getItem(CONTENT_DETAIL_STORAGE_KEY);
    }, []);

    // localStorage에서 저장된 URL 삭제
    const clearSavedContentUrl = useCallback(() => {
        if (typeof window !== "undefined") {
            localStorage.removeItem(CONTENT_DETAIL_STORAGE_KEY);
        }
    }, []);

    // 저장된 콘텐츠 URL로 리다이렉트
    const redirectToSavedContent = useCallback(() => {
        const savedUrl = getSavedContentUrl();
        if (savedUrl) {
            clearSavedContentUrl();
            router.push(
                {
                    pathname: router.pathname,
                    query: router.query,
                    hash: savedUrl.replace("#", "")
                },
                undefined,
                { scroll: false }
            );
            return true;
        }
        return false;
    }, [router, getSavedContentUrl, clearSavedContentUrl]);

    return {
        isOpen,
        contentId,
        content,
        loading,
        openContentModal,
        closeContentModal,
        openMethod: openMethodRef.current,
        contentSection: contentSectionRef.current,
        saveContentUrlForRedirect,
        getSavedContentUrl,
        clearSavedContentUrl,
        redirectToSavedContent,
        // 네비게이션 관련
        handleNavigate,
        canNavigatePrev,
        canNavigateNext
    };
};

export { CONTENT_DETAIL_STORAGE_KEY };
